import { AxiosResponse } from 'axios';
import {
  borgMigrationAuthId,
  OpenAmAuthenticateResponse,
  OpenAmAuthenticateResponseOfBorgMigra,
  OpenAmAuthenticateResponseOfLoginWithAuthenticatorCode,
  OpenAmAuthenticateResponseOfLoginWithSMSCode,
  OpenAmAuthenticateResponseOfLoginWithUsernamePassword,
  OpenAmAuthenticateResponseOfYoloOtpCode,
  OpenAMSessionCheckResponse,
} from '../@types/OpenAmInitialData';
import { AuthenticationResource } from './authenticationResource';
import { axiosInstance } from './axiosInstance';

const openAmJsonApiPrefix = `/openam/json/realms`;

const url = new URL(window.location.href);

const realm = (s => {
  const realm = s.get('realm') || '/';
  if (!realm || realm === '/') {
    return 'root';
  }
  return realm.replace('/', '');
})(url.searchParams);

const baseUrl = (() => {
  if (realm !== 'root') {
    return [openAmJsonApiPrefix, 'root', 'realms', realm, ''].join('/');
  }
  return [openAmJsonApiPrefix, 'root', ''].join('/');
})();

const service = (s => {
  return s.get('service');
})(url.searchParams);

const goto = (s => {
  return s.get('goto');
})(url.searchParams);

const theme = (s => {
  return s.get('theme');
})(url.searchParams);

const mode = (s => {
  return s.get('mode');
})(url.searchParams);

const app = (s => {
  return s.get('app');
})(url.searchParams);

const authenticateParams = [
  realm !== 'root' ? `realm=/${realm}` : null,
  service ? `service=${service}` : null,
  service ? `authIndexType=service` : null,
  service ? `authIndexValue=${service}` : null,
  goto ? `goto=${encodeURIComponent(goto)}` : null,
  theme ? `theme=${theme}` : null,
  mode ? `mode=${mode}` : null,
  app ? `app=${app}` : null,
  `ytr`,
].filter(Boolean);

const resendable = (
  authId: string,
  otpCode: string,
  resend: boolean,
  stageName: string,
  name1: string,
  name2: string,
): OpenAmAuthenticateResponseOfYoloOtpCode => {
  return {
    authId,
    template: '',
    stage: stageName,
    header: '',
    infoText: [''],
    callbacks: [
      {
        type: 'NameCallback',
        output: [{ name: 'prompt', value: name1 }],
        input: [{ name: 'IDToken1', value: otpCode }],
      },
      {
        type: 'ChoiceCallback',
        output: [{ name: 'prompt', value: name2 }],
        input: [{ name: 'IDToken2', value: resend ? 1 : 0 }],
      },
    ],
  };
};

const otpData = (authId: string, otpCode: string, resend: boolean) =>
  resendable(authId, otpCode, resend, 'YettelYoloOtp2', 'yolootp-code', 'yolootp-action');
const msisdnData = (authId: string, otpCode: string, resend: boolean) =>
  resendable(
    authId,
    otpCode,
    resend,
    'YettelMsisdnVerificator3',
    'yettel-msisdn-verificator-code',
    'msisdn-verificator-action',
  );

const post = async (datas: OpenAmAuthenticateResponseOfYoloOtpCode): Promise<OpenAmAuthenticateResponse> => {
  return withAnalytics(
    await axiosInstance.post<any, AxiosResponse<OpenAmAuthenticateResponse>, OpenAmAuthenticateResponseOfYoloOtpCode>(
      `${baseUrl}authenticate?${authenticateParams.join('&')}`,
      datas,
    ),
  );
};

const header = (headers: Record<string, string>, key: string) => {
  const foundKey = Object.keys(headers)
    .map(k => k.toLowerCase())
    .find(k => k === key.toLowerCase());
  if (foundKey) {
    return headers[foundKey];
  } else {
    return undefined;
  }
};

const withAnalytics = (response: AxiosResponse<OpenAmAuthenticateResponse>): OpenAmAuthenticateResponse => {
  const accountId = header(response.headers, 'x-ynltx-ac');
  const mfa = header(response.headers, 'x-ynltx-mf');
  const userId = header(response.headers, 'x-ynltx-ui');
  return {
    accountId: accountId ? accountId : undefined,
    mfa: !!mfa,
    uid: userId ? userId : undefined,
    ...response.data,
  };
};

export const openAmClient: AuthenticationResource = {
  async checkSession() {
    return await axiosInstance.post<OpenAMSessionCheckResponse>(`/openam/json/users?_action=idFromSession`, {});
  },
  async getInitialData() {
    const url = new URL(window.location.href);
    const migraAuthId = url.searchParams.get(borgMigrationAuthId);
    if (migraAuthId) {
      return await axiosInstance.post<
        any,
        AxiosResponse<OpenAmAuthenticateResponse>,
        OpenAmAuthenticateResponseOfBorgMigra
      >(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId: migraAuthId,
        header: '',
        infoText: [],
        stage: 'YettelBorgMigration3',
        template: '',
        callbacks: [
          {
            type: 'HiddenValueCallback',
            output: [{ name: 'value', value: '' }],
            input: [{ name: 'IDToken1', value: 'migraUrl' }],
          },
        ],
      });
    } else {
      return await axiosInstance.post<OpenAmAuthenticateResponseOfLoginWithUsernamePassword>(
        `${baseUrl}authenticate?${authenticateParams.join('&')}`,
        {},
      );
    }
  },
  async postLogin(
    authId: string,
    email: string,
    password: string,
    captchaValue: string,
  ): Promise<OpenAmAuthenticateResponse> {
    return withAnalytics(
      await axiosInstance.post<
        any,
        AxiosResponse<OpenAmAuthenticateResponse>,
        OpenAmAuthenticateResponseOfLoginWithUsernamePassword
      >(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId,
        template: '',
        stage: 'YettelAccount2',
        header: '',
        infoText: ['username', 'password'],
        callbacks: [
          {
            type: 'NameCallback',
            output: [{ name: 'prompt', value: 'E-mail cím' }],
            input: [{ name: 'IDToken1', value: email }],
          },
          {
            type: 'PasswordCallback',
            output: [{ name: 'prompt', value: 'Jelszó' }],
            input: [{ name: 'IDToken2', value: password }],
          },
          {
            type: 'HiddenValueCallback',
            output: [{ name: 'prompt', value: 'Jelszó' }],
            input: [{ name: 'IDToken3', value: captchaValue }],
          },
        ],
      }),
    );
  },
  async postPhoneNumber(authId: string, phoneNumber: string, stageName: string): Promise<OpenAmAuthenticateResponse> {
    return withAnalytics(
      await axiosInstance.post(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId,
        template: '',
        stage: stageName,
        header: '',
        infoText: [''],
        callbacks: [
          {
            type: 'NameCallback',
            output: [{ name: 'prompt', value: 'sms-number' }],
            input: [{ name: 'IDToken1', value: phoneNumber }],
          },
        ],
      }),
    );
  },
  async postSMSCode(authId: string, smsCode: string): Promise<OpenAmAuthenticateResponse> {
    return withAnalytics(
      await axiosInstance.post<
        any,
        AxiosResponse<OpenAmAuthenticateResponse>,
        OpenAmAuthenticateResponseOfLoginWithSMSCode
      >(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId,
        template: '',
        stage: 'YettelSms4',
        header: '',
        infoText: [''],
        callbacks: [
          {
            type: 'NameCallback',
            output: [{ name: 'prompt', value: 'yettel-sms-code' }],
            input: [{ name: 'IDToken1', value: smsCode }],
          },
        ],
      }),
    );
  },
  async postSMSCodeChange2FA(authId: string, smsCode: string): Promise<OpenAmAuthenticateResponse> {
    return withAnalytics(
      await axiosInstance.post<
        any,
        AxiosResponse<OpenAmAuthenticateResponse>,
        OpenAmAuthenticateResponseOfLoginWithSMSCode
      >(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId,
        template: '',
        stage: 'YettelSms7',
        header: '',
        infoText: [''],
        callbacks: [
          {
            type: 'NameCallback',
            output: [{ name: 'prompt', value: 'yettel-sms-code2' }],
            input: [{ name: 'IDToken1', value: smsCode }],
          },
        ],
      }),
    );
  },
  async postAuthenticatorCode(authId: string, authenticatorCode: string): Promise<OpenAmAuthenticateResponse> {
    return withAnalytics(
      await axiosInstance.post<
        any,
        AxiosResponse<OpenAmAuthenticateResponse>,
        OpenAmAuthenticateResponseOfLoginWithAuthenticatorCode
      >(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId,
        template: '',
        stage: 'YettelTotp2',
        header: '',
        infoText: [''],
        callbacks: [
          {
            type: 'NameCallback',
            output: [{ name: 'prompt', value: 'totp-code' }],
            input: [{ name: 'IDToken1', value: authenticatorCode }],
          },
        ],
      }),
    );
  },

  async postYoloOtpCode(authId: string, otpCode: string): Promise<OpenAmAuthenticateResponse> {
    return post(otpData(authId, otpCode, false));
  },

  async reSendYoloOtpCode(authId: string): Promise<OpenAmAuthenticateResponse> {
    return post(otpData(authId, '', true));
  },

  async postMSISDNVerificatorCode(authId: string, otpCode: string): Promise<OpenAmAuthenticateResponse> {
    return post(msisdnData(authId, otpCode, false));
  },

  async reSendMSISDNVerificatorCode(authId: string): Promise<OpenAmAuthenticateResponse> {
    return post(msisdnData(authId, '', true));
  },

  async postTestLogin(authId: string): Promise<OpenAmAuthenticateResponse> {
    return withAnalytics(
      await axiosInstance.post(`${baseUrl}authenticate?${authenticateParams.join('&')}`, {
        authId,
        template: '',
        stage: 'YettelMockTestLogin1',
        header: '',
        infoText: [''],
        callbacks: [
          {
            type: 'ConfirmationCallback',
            output: [{ name: 'prompt', value: 'test-login-confirmation' }],
            input: [{ name: 'IDToken1', value: 0 }],
          },
        ],
      }),
    );
  },
};
